import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  MessageSquare, Type, Languages, Search, FileText, ListChecks, Pencil, HelpCircle,
  Sparkles, RefreshCw, Users,
  Glasses, Package, Cable, BookOpen, Feather, Puzzle,
  ChevronLeft, ChevronRight,
  RotateCcw, Eye, Truck, Check,
} from 'lucide-react';
import { GlassPage } from '../components/chrome';
import { ProductCards } from '../components/products';

const PRICE = 500;
const PRESCRIPTION_UPCHARGE = 100;
// Pre-ordering saves this much against the eventual launch price, whichever
// lens you pick.
const EARLY_ACCESS_SAVING = 100;

// Lens choice drives both the total and which hosted Stripe link we send the
// buyer to. Payment links carry a fixed amount, so each price has its own link
// and the two must stay in sync with the `price` beside them.
const LENS_OPTIONS = [
  {
    id: 'standard',
    label: 'Non-prescription',
    desc: 'Clear lenses, no correction',
    price: PRICE,
    checkoutUrl: 'https://buy.stripe.com/cNi5kC6YI4Ml4BHgLne3e07',
  },
  {
    id: 'prescription',
    label: 'Prescription',
    desc: 'Made to your script',
    price: PRICE + PRESCRIPTION_UPCHARGE,
    checkoutUrl: 'https://buy.stripe.com/28E6oG5UE2Ed3xDgLne3e08',
  },
];

const FEATURES = [
  { icon: MessageSquare, title: 'Caption', desc: 'Loud room, four people talking. Every word gets subtitled.' },
  { icon: Type, title: 'Recall', desc: 'Mid-sentence, you forget the word. It appears on the lens instantly.' },
  { icon: Languages, title: 'Translate', desc: 'The cashier speaks Italian, but you see English automatically.' },
  { icon: Search, title: 'Ask', desc: 'You or someone else asks a question. The answer is there instantly.' },
  { icon: FileText, title: 'Teleprompt', desc: 'Your script scrolls as you speak. You keep eye contact the whole presentation.' },
  { icon: ListChecks, title: 'Recap', desc: 'Forgot something after a conversation? The summary is waiting for you.' },
  { icon: Pencil, title: 'Note', desc: 'Say the idea. It is written down before you forget it.' },
  { icon: HelpCircle, title: 'Question', desc: 'Asked too many things at once? It is all displayed for you to answer.' },
];

const MARKETPLACE = [
  { icon: Sparkles, title: 'Add abilities', desc: 'Find what fits your day and add it in a tap. It lands on your lens with nothing to set up.' },
  { icon: RefreshCw, title: 'Always growing', desc: 'Updates come over the air, so the pair you buy this year keeps learning new tricks the next.' },
  { icon: Users, title: 'Built by many', desc: 'Anyone can build for the lens, so what KorsaOS can do never stops growing.' },
];

// Product gallery. Files live in public/photos/ and are square, so the frame
// below shows them uncropped. Swap the images there (keep the filenames).
const ORDER_PHOTOS = [
  { src: '/photos/reserve-front.png', alt: 'Korsa Glasses, front view on model' },
  { src: '/photos/reserve-angle.png', alt: 'Korsa Glasses, three-quarter view on model' },
  { src: '/photos/reserve-product.png', alt: 'Korsa Glasses product close-up' },
];

// Short reassurance row under the buy button.
const ASSURANCES = [
  { icon: RotateCcw, title: 'Fully refundable', desc: 'Cancel any time before your glasses ship and we return every dollar.' },
  { icon: Eye, title: 'Prescription ready', desc: `Add prescription lenses for $${PRESCRIPTION_UPCHARGE}. We collect your script before shipping.` },
  { icon: Truck, title: 'Delivers Q4 2026', desc: 'US deliveries first. Pre-orders ship in the order they were placed.' },
  { icon: RefreshCw, title: 'Improves over time', desc: 'KorsaOS updates arrive over the air, so the pair you buy keeps gaining abilities.' },
];

const BOX_ITEMS = [
  { icon: Glasses, label: 'Korsa Glasses' },
  { icon: Package, label: 'Charging case' },
  { icon: Cable, label: 'USB-C charging cable' },
  { icon: Feather, label: 'Microfiber cleaning cloth' },
  { icon: Puzzle, label: 'Replacement pieces' },
  { icon: BookOpen, label: 'Quick start guide' },
];

// Only facts we can stand behind today. Hardware numbers (battery, weight,
// display) go here once they are final.
const DETAILS = [
  ['Price', `$${PRICE} non-prescription, $${PRICE + PRESCRIPTION_UPCHARGE} with prescription lenses`],
  ['Launch price', `$${EARLY_ACCESS_SAVING} more once the early access window closes`],
  ['Availability', 'Delivers Q4 2026, US first'],
  ['Prescription', `Optional, adds $${PRESCRIPTION_UPCHARGE}`],
  ['Software', 'KorsaOS, with over-the-air updates'],
  ['Marketplace', 'Third-party abilities you add in a tap'],
  ['Support', 'support@korsa.tech'],
];

const FAQS = [
  { q: 'Where does my money go?', a: "It's the full price of your pair, held against your pre-order. If you cancel any time before shipping you get all of it back." },
  { q: 'How do refunds work?', a: 'Email support@korsa.tech. We refund the full amount to your original payment method, no reason needed.' },
  { q: 'What do I pay in total?', a: '$500 for non-prescription, or $600 with prescription lenses. You pay it today, it is fully refundable, and nothing more is due when your glasses ship. Prices rise by $100 once the early access window closes, so pre-ordering now saves you $100 and secures your pair in case of a stock shortage.' },
  { q: 'When do the glasses ship?', a: "Delivery is planned for Q4 2026. Pre-orders ship first, in the order they were placed, and we'll email you as the date firms up." },
  { q: 'Can I use my own prescription?', a: 'Yes. Choose the prescription option at checkout and it adds $100 to your order. Once your pair is ready to ship we email you to collect your prescription details.' },
  { q: 'What hardware are the glasses?', a: 'Korsa Glasses are manufactured for Korsa Inc., with KorsaOS, our operating system, integrated in.' },
  { q: 'Is this a finished product?', a: 'This is an early pre-order while we finish up our final touches. You are buying into the first production run.' },
];

// --- Small building blocks ---

const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 ${className}`}>
    {children}
  </p>
);

const SectionHeading = ({ eyebrow, title, lead, className = '' }) => (
  <div className={`max-w-2xl ${className}`}>
    {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
    <h2 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase text-zinc-900">{title}</h2>
    {lead && <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed">{lead}</p>}
  </div>
);

const Accordion = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={item.q} className="rounded-2xl bg-white/70 border border-white overflow-hidden">
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
            >
              <span className="text-base md:text-lg font-bold text-zinc-900">{item.q}</span>
              <Plus size={18} className={`shrink-0 text-zinc-500 transition-transform duration-300 ease-out ${isOpen ? 'rotate-45' : ''}`} />
            </button>
            <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
              <div className="overflow-hidden">
                <p className="px-6 pb-5 text-sm md:text-base text-zinc-600 leading-relaxed">{item.a}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Page ---

const Order = () => {
  const [redirecting, setRedirecting] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lensId, setLensId] = useState(LENS_OPTIONS[0].id);

  const lens = LENS_OPTIONS.find((o) => o.id === lensId);
  const total = lens.price;

  // Lenis owns the scroll position, so drive in-page links through the shared
  // instance; fall back to native smooth scroll.
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };

  // Send buyers to the hosted Stripe link that matches their lens choice.
  const startCheckout = () => {
    if (redirecting) return;
    setRedirecting(true);
    window.location.assign(lens.checkoutUrl);
  };

  const BuyButton = ({ className = '' }) => (
    <button
      type="button"
      onClick={startCheckout}
      disabled={redirecting}
      className={`btn-dark w-full py-4 text-xs ${redirecting ? 'opacity-60 cursor-wait' : ''} ${className}`}
    >
      {redirecting ? 'Opening checkout...' : `Pre-order now · $${total}`}
    </button>
  );

  return (
    <GlassPage>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 md:pt-28">

        {/* Breadcrumb */}
        <nav className="mb-4 text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
          <Link to="/" className="hover:text-zinc-900 transition-colors">Korsa</Link>
          <span className="mx-2 text-zinc-300">/</span>
          <span className="text-zinc-900">Glasses</span>
        </nav>

        {/* ---------- Hero: gallery + buy box ---------- */}
        <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6 lg:gap-10 items-start">

          {/* Gallery */}
          <div>
            <div className="group relative glass overflow-hidden aspect-square">
              {ORDER_PHOTOS.map((p, i) => (
                <img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${i === photoIdx ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}

              <button
                type="button"
                onClick={() => setPhotoIdx((i) => (i - 1 + ORDER_PHOTOS.length) % ORDER_PHOTOS.length)}
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-[0_4px_16px_rgba(31,38,135,0.15)] flex items-center justify-center text-zinc-800 opacity-0 group-hover:opacity-100 hover:bg-white transition-all duration-200"
              >
                <ChevronLeft size={20} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                onClick={() => setPhotoIdx((i) => (i + 1) % ORDER_PHOTOS.length)}
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-md border border-white shadow-[0_4px_16px_rgba(31,38,135,0.15)] flex items-center justify-center text-zinc-800 opacity-0 group-hover:opacity-100 hover:bg-white transition-all duration-200"
              >
                <ChevronRight size={20} strokeWidth={2.2} />
              </button>
            </div>

            {/* Thumbnails sit below the frame so they never cover the product */}
            <div className="mt-3 grid grid-cols-3 gap-3">
              {ORDER_PHOTOS.map((p, i) => (
                <button
                  key={p.src}
                  type="button"
                  onClick={() => setPhotoIdx(i)}
                  aria-label={`Show ${p.alt}`}
                  aria-pressed={i === photoIdx}
                  className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all duration-300 ${i === photoIdx ? 'border-zinc-900' : 'border-transparent opacity-60 hover:opacity-100'}`}
                >
                  <img src={p.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Buy box */}
          <div className="glass px-6 md:px-9 py-8 md:py-10 lg:sticky lg:top-24">
            <Eyebrow>Pre-order · Early access</Eyebrow>

            <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tighter uppercase leading-[0.95] text-zinc-900">
              Korsa Glasses
            </h1>

            {/* Price */}
            <div className="mt-7 flex items-end gap-3">
              <span className="text-4xl md:text-5xl font-bold tracking-tighter text-zinc-900">${total}</span>
              <span className="text-lg font-semibold text-zinc-400 line-through mb-1">
                ${total + EARLY_ACCESS_SAVING}
              </span>
              <span className="mb-1.5 rounded-full bg-zinc-900 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">
                Save ${EARLY_ACCESS_SAVING}
              </span>
            </div>
            <p className="mt-2 text-sm text-zinc-600">
              Paid today and fully refundable. Nothing more is due when your glasses ship.
            </p>

            {/* Lens choice */}
            <div className="mt-7">
              <div className="flex items-baseline justify-between mb-3">
                <Eyebrow>Lenses</Eyebrow>
                <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                  {lens.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Lens type">
                {LENS_OPTIONS.map((opt) => {
                  const selected = opt.id === lensId;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setLensId(opt.id)}
                      className={`rounded-2xl border-2 px-4 py-3.5 text-left transition-all duration-200 ${
                        selected
                          ? 'border-zinc-900 bg-white'
                          : 'border-zinc-900/10 bg-white/50 hover:border-zinc-900/30'
                      }`}
                    >
                      <span className="block text-sm font-bold text-zinc-900 leading-tight">{opt.label}</span>
                      <span className="block text-[11px] text-zinc-500 mt-1 leading-snug">{opt.desc}</span>
                      <span className="block text-sm font-bold text-zinc-900 mt-2">
                        {opt.price === PRICE ? `$${opt.price}` : `+$${opt.price - PRICE}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Inclusions */}
            <ul className="mt-6 space-y-2.5 border-t border-zinc-900/5 pt-6">
              {[
                lensId === 'prescription'
                  ? 'Prescription lenses made to your script'
                  : `Prescription lenses available for $${PRESCRIPTION_UPCHARGE}`,
                'Charging case and accessories in the box',
                'KorsaOS with lifetime over-the-air updates',
                `Locks your price before it rises $${EARLY_ACCESS_SAVING}`,
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check size={16} strokeWidth={2.6} className="mt-0.5 shrink-0 text-zinc-900" />
                  <span className="text-sm text-zinc-700 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7">
              <BuyButton />
              <p className="mt-3 text-center text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                Secure checkout with Stripe
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-zinc-900/5 flex flex-wrap gap-x-5 gap-y-2">
              <button type="button" onClick={() => scrollTo('details')} className="text-[10px] uppercase tracking-widest font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
                Details
              </button>
              <button type="button" onClick={() => scrollTo('korsaos')} className="text-[10px] uppercase tracking-widest font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
                What it does
              </button>
              <button type="button" onClick={() => scrollTo('faq')} className="text-[10px] uppercase tracking-widest font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
                FAQ
              </button>
            </div>
          </div>
        </section>

        {/* ---------- Assurances ---------- */}
        <section className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ASSURANCES.map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.title} className="glass px-6 py-6">
                <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                <h3 className="text-base font-bold tracking-tight text-zinc-900 mb-1.5">{a.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{a.desc}</p>
              </div>
            );
          })}
        </section>
      </div>

      {/* ---------- What it does ---------- */}
      <section id="korsaos" className="py-16 md:py-24 px-4 md:px-6 scroll-mt-24">
        <div className="glass max-w-[1400px] mx-auto px-8 md:px-16 py-14 md:py-20">
          <SectionHeading
            eyebrow="KorsaOS"
            title="What it does"
            lead="Eight abilities that run on the lens, built for the moments where words get hard. They work together, and they stay out of the way until you need them."
          />

          <div className="mt-10 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 hover:-translate-y-1 transition-transform duration-300">
                  <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                  <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{f.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Glasses + App ---------- */}
      <ProductCards />

      {/* ---------- In the box + details ---------- */}
      <section id="details" className="py-16 md:py-24 px-4 md:px-6 scroll-mt-24">
        <div className="glass max-w-[1400px] mx-auto px-8 md:px-16 py-14 md:py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">

            <div>
              <SectionHeading eyebrow="In the box" title="What's included" />
              <div className="mt-8 grid sm:grid-cols-2 gap-3 md:gap-4">
                {BOX_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-center gap-3 rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.10)] px-5 py-4">
                      <Icon size={20} strokeWidth={2} className="text-zinc-600 shrink-0" />
                      <span className="text-sm md:text-base font-bold text-zinc-900">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:border-l lg:border-zinc-900/5 lg:pl-16">
              <SectionHeading eyebrow="Details" title="At a glance" />
              <dl className="mt-8 divide-y divide-zinc-900/5">
                {DETAILS.map(([label, value]) => (
                  <div key={label} className="py-3.5 grid grid-cols-[7.5rem_1fr] gap-4 items-baseline">
                    <dt className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{label}</dt>
                    <dd className="text-sm text-zinc-700 leading-relaxed">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-sm text-zinc-500 leading-relaxed">
                Questions about your prescription? Email{' '}
                <a href="mailto:support@korsa.tech" className="text-zinc-900 underline underline-offset-2">support@korsa.tech</a>.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ---------- Marketplace ---------- */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <div className="glass max-w-[1400px] mx-auto px-8 md:px-16 py-14 md:py-20">
          <SectionHeading
            eyebrow="Marketplace"
            title="One pair that keeps getting better"
            lead="New abilities arrive the way apps do on a phone, so the glasses you pre-order today are not the ones you will be wearing in a year."
          />
          <div className="mt-10 md:mt-14 grid sm:grid-cols-3 gap-4 md:gap-5">
            {MARKETPLACE.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.title} className="rounded-2xl bg-white/80 border border-white shadow-[0_10px_30px_rgba(31,38,135,0.12)] p-5 md:p-6 hover:-translate-y-1 transition-transform duration-300">
                  <Icon size={20} strokeWidth={2} className="text-zinc-600 mb-3" />
                  <h3 className="text-lg font-bold tracking-tight text-zinc-900 mb-1.5">{m.title}</h3>
                  <p className="text-sm text-zinc-600 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="py-16 md:py-24 px-4 md:px-6 scroll-mt-24">
        <div className="glass max-w-[1400px] mx-auto px-8 md:px-16 py-14 md:py-20">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16">
            <SectionHeading
              eyebrow="Support"
              title="Questions"
              lead="Everything about the money, the timeline, and what you are actually buying."
            />
            <Accordion items={FAQS} />
          </div>
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section className="pb-20 md:pb-28 px-4 md:px-6">
        <div className="glass-strong max-w-[1400px] mx-auto px-8 md:px-16 py-14 md:py-20 text-center">
          <Eyebrow className="mb-5">Early access</Eyebrow>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter uppercase leading-[0.95] text-zinc-900">
            Lock your pair at ${total}
          </h2>
          <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed max-w-xl mx-auto">
            Paid today, fully refundable until your glasses ship. Delivers Q4 2026,
            and the price rises ${EARLY_ACCESS_SAVING} once the early access window closes.
          </p>
          <div className="mt-8 max-w-xs mx-auto">
            <BuyButton />
            <p className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              Secure checkout with Stripe
            </p>
          </div>
        </div>
      </section>
    </GlassPage>
  );
};

export const OrderThanks = () => (
  <GlassPage>
    <div className="max-w-lg mx-auto px-4 md:px-6 pt-28 md:pt-36">
      <section className="glass px-8 md:px-12 py-12 md:py-16 text-center">
        <div className="mx-auto w-16 h-16 rounded-full border border-green-500/30 bg-green-500/10 flex items-center justify-center text-green-600 mb-8">
          <Check size={28} strokeWidth={2.5} />
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-zinc-900 mb-4">Pre-order confirmed</h1>
        <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-4">
          Your payment is held, not spent. It stays fully refundable until your glasses
          ship: email <a href="mailto:support@korsa.tech" className="text-zinc-900 underline underline-offset-2">support@korsa.tech</a> anytime
          and we'll return it, no reason needed.
        </p>
        <p className="text-sm md:text-base text-zinc-600 leading-relaxed mb-10">
          Delivery is planned for Q4 2026. That's an estimate, and we'll email you
          updates as the date firms up. If you chose prescription lenses we'll collect
          your script before your pair ships. Nothing more is due.
        </p>
        <Link to="/" className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900 transition-colors">
          Back to home →
        </Link>
      </section>
    </div>
  </GlassPage>
);

export default Order;
